/**
 * Knowledge Base article detail — public reader at /knowledgebase/:slug.
 *
 * Body HTML is sanitized at write time on the server (see
 * adminKnowledgeBasePrisma.js sanitize-html allowlist), so we render the
 * stored string directly via dangerouslySetInnerHTML. The server is the
 * source of truth for what's safe to render.
 *
 * Injects JSON-LD Article schema for Google. Same CRA/SSR caveat as the
 * index page — Google's crawler picks this up, social-share previews do not.
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  BookOpen,
  ArrowLeft,
  MessageCircle,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import './KnowledgeBase.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const WHATSAPP_NUMBER = '2348175658439';

const buildWhatsAppLink = (articleTitle) => {
  const text = articleTitle
    ? `Hi! I'm reading "${articleTitle}" on the Revstar help center and still need help.`
    : "Hi! I'm using Revstar and need help with…";
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
};

const setMeta = (selector, attr, value) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const m = selector.match(/(name|property)="([^"]+)"/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const STRUCTURED_DATA_ID = 'kb-article-jsonld';

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const KnowledgeBaseArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setArticle(null);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/knowledgebase/articles/${encodeURIComponent(slug)}`);
        if (res.status === 404) {
          if (!cancelled) setStatus('notfound');
          return;
        }
        const data = await res.json();
        if (!res.ok || !data.success) {
          if (!cancelled) setStatus('error');
          return;
        }
        if (!cancelled) {
          setArticle(data.data);
          setStatus('ok');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // SEO meta + structured data
  useEffect(() => {
    if (!article) return;

    const title = `${article.title} — Revstar Help Center`;
    const description = article.excerpt || `${article.title} — guide on the Revstar help center.`;
    const canonical = `${window.location.origin}/knowledgebase/${article.slug}`;

    document.title = title;
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', article.title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:type"]', 'content', 'article');
    setMeta('meta[property="og:url"]', 'content', canonical);
    setMeta('meta[name="twitter:card"]', 'content', 'summary');
    setMeta('meta[name="twitter:title"]', 'content', article.title);
    setMeta('meta[name="twitter:description"]', 'content', description);

    // JSON-LD Article schema
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description,
      datePublished: article.publishedAt || article.createdAt,
      dateModified: article.updatedAt,
      mainEntityOfPage: canonical,
      author: { '@type': 'Organization', name: 'Revstar' },
      publisher: { '@type': 'Organization', name: 'Revstar' },
    };
    let script = document.getElementById(STRUCTURED_DATA_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = STRUCTURED_DATA_ID;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);

    return () => {
      const existing = document.getElementById(STRUCTURED_DATA_ID);
      if (existing) existing.remove();
    };
  }, [article]);

  const renderTopbar = () => (
    <header className="kb-topbar">
      <div className="kb-topbar-inner">
        <Link to="/knowledgebase" className="kb-brand">
          <BookOpen size={18} />
          <span>Revstar Help<span className="kb-brand-sub">Center</span></span>
        </Link>
        <Link to="/overview" className="kb-topbar-link">
          <ArrowLeft size={14} />
          <span>Back to app</span>
        </Link>
      </div>
    </header>
  );

  if (status === 'loading') {
    return (
      <div className="kb-shell">
        {renderTopbar()}
        <main className="kb-article-wrap">
          <div className="kb-loading">
            <RefreshCw size={14} className="kb-spin" />
            <span>Loading article…</span>
          </div>
        </main>
      </div>
    );
  }

  if (status === 'notfound' || status === 'error') {
    return (
      <div className="kb-shell">
        {renderTopbar()}
        <main className="kb-article-wrap">
          <div className="kb-notfound">
            <h2>{status === 'notfound' ? 'Article not found' : 'Something went wrong'}</h2>
            <p>
              {status === 'notfound'
                ? 'This article may have been moved or unpublished.'
                : "We couldn't load this article. Please try again in a moment."}
            </p>
            <Link to="/knowledgebase" className="kb-back-link">
              <ArrowLeft size={14} />
              <span>Back to Help Center</span>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const published = formatDate(article.publishedAt);
  const updated = formatDate(article.updatedAt);

  return (
    <div className="kb-shell">
      {renderTopbar()}

      <main className="kb-article-wrap">
        <nav className="kb-breadcrumb" aria-label="Breadcrumb">
          <Link to="/knowledgebase">Help Center</Link>
          {article.category && (
            <>
              <ChevronRight size={12} className="kb-breadcrumb-sep" aria-hidden="true" />
              <span>{article.category.name}</span>
            </>
          )}
        </nav>

        <header className="kb-article-head">
          <h1 className="kb-article-title">{article.title}</h1>
          {article.excerpt && <p className="kb-article-excerpt">{article.excerpt}</p>}
          <div className="kb-article-meta">
            {published && <span>Published {published}</span>}
            {updated && updated !== published && (
              <>
                <span className="kb-article-meta-dot" />
                <span>Updated {updated}</span>
              </>
            )}
          </div>
        </header>

        <article
          className="kb-article-body"
          // The body string is server-sanitized via sanitize-html allowlist
          // at write time; rendering directly is safe.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: article.body || '' }}
        />

        {Array.isArray(article.tags) && article.tags.length > 0 && (
          <div className="kb-article-tags">
            {article.tags.map((t) => (
              <span key={t} className="kb-article-tag">#{t}</span>
            ))}
          </div>
        )}

        <footer className="kb-article-foot">
          <section className="kb-human-cta">
            <div className="kb-human-cta-text">
              <p className="kb-human-cta-title">Was this helpful?</p>
              <p className="kb-human-cta-sub">If you still need a hand, message our team on WhatsApp.</p>
            </div>
            <a
              href={buildWhatsAppLink(article.title)}
              target="_blank"
              rel="noopener noreferrer"
              className="kb-human-cta-btn"
            >
              <MessageCircle size={16} />
              <span>Talk to a human</span>
            </a>
          </section>

          <Link to="/knowledgebase" className="kb-back-link">
            <ArrowLeft size={14} />
            <span>Back to Help Center</span>
          </Link>
        </footer>
      </main>
    </div>
  );
};

export default KnowledgeBaseArticle;
